import React, { useState } from 'react';
import GuidedRemovalTypeScreen from './GuidedRemovalTypeScreen';

function RemovalTypeStep({ currentService, serviceAddOns, selectedCenter, selectedMandatoryAddOns, onToggleAddOn, serviceCategory, onSkipAddOn, selectedOptionalAddOns, onToggleOptionalAddOn, onContinue, onBack }) {
  const mandatoryAddOns = serviceAddOns[currentService?.id]?.mandatory || [];
  const optionalAddOns = serviceAddOns[currentService?.id]?.optional || [];
  const isWaxing = (serviceCategory || '').toLowerCase().includes('wax');
  const [expandedTooltip, setExpandedTooltip] = useState(null);

  function handleStripWaxSelect() {
    const currentSelected = selectedMandatoryAddOns.filter(a => a.parentServiceId === currentService.id);
    currentSelected.forEach(a => onToggleAddOn(a, currentService.id));
  }

  function handleWaxAddOnSelect(addOn) {
    const currentSelected = selectedMandatoryAddOns.filter(a => a.parentServiceId === currentService.id);
    currentSelected.forEach(a => {
      if (a.id !== addOn.id) onToggleAddOn(a, currentService.id);
    });
    const alreadySelected = selectedMandatoryAddOns.some(a => a.id === addOn.id && a.parentServiceId === currentService.id);
    if (!alreadySelected) onToggleAddOn(addOn, currentService.id);
  }

  if (isWaxing) {
    const hasSelection = selectedMandatoryAddOns.some(a => a.parentServiceId === currentService?.id);
    return (
      <div className="step-content">
        <h1 className="step-title">{currentService?.name?.toUpperCase() || 'CHOOSE YOUR WAX'}</h1>
        <p className="step-subtitle">{selectedCenter?.name}</p>

        <div className="guided-section-header" style={{ marginTop: 16, marginBottom: 8, fontSize: 13, fontWeight: 700, letterSpacing: '1.5px', color: '#1a1a1a', textTransform: 'uppercase', padding: '8px 10px', background: '#f3eef0', borderRadius: 4 }}>
          Choose your wax
        </div>
        <div className="addons-list">
          <div className="addons-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
            {/* Strip Wax — included, no add-on */}
            <div
              className={`addon-card ${!hasSelection ? 'selected' : ''}`}
              onClick={handleStripWaxSelect}
              style={{ position: 'relative' }}
            >
              <div className={`addon-card-checkbox ${!hasSelection ? 'checked' : ''}`}>
                {!hasSelection && '✓'}
              </div>
              <div className="addon-info">
                <div className="addon-name">Strip Wax</div>
                <div className="addon-desc">Quick and effective</div>
                <div className="addon-bottom">
                  <span className="addon-price" style={{ color: '#4caf50' }}>Included</span>
                </div>
              </div>
            </div>

            {/* Hard wax add-on options */}
            {mandatoryAddOns.map(addOn => {
              const isSelected = selectedMandatoryAddOns.some(a => a.id === addOn.id && a.parentServiceId === currentService.id);
              const displayName = addOn.name?.replace(/\s*-\s*(Brazilian|Bikini|French|Full Leg|Half Leg|Underarm|Arm|Back|Chest|Brow|Lip|Chin|Face|Nose).*$/i, '') || addOn.name;
              return (
                <div key={addOn.id}
                  className={`addon-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleWaxAddOnSelect(addOn)}
                  style={{ position: 'relative' }}
                >
                  <div className={`addon-card-checkbox ${isSelected ? 'checked' : ''}`}>
                    {isSelected && '✓'}
                  </div>
                  <div className="addon-info">
                    <div className="addon-name">{displayName}</div>
                    <div className="addon-desc">Gentler. Better for sensitive skin.</div>
                    <button
                      type="button"
                      className="addon-tooltip-link"
                      onClick={(e) => { e.stopPropagation(); setExpandedTooltip(prev => prev === addOn.id ? null : addOn.id); }}
                      aria-expanded={expandedTooltip === addOn.id}
                    >
                      {'\u24D8'} Why hard wax?
                    </button>
                    {expandedTooltip === addOn.id && (
                      <div className="addon-tooltip-panel" onClick={(e) => e.stopPropagation()}>
                        Hard wax grips the hair, not the skin — less irritation, less redness.
                      </div>
                    )}
                    <div className="addon-bottom">
                      {addOn.duration > 0 && <span className="addon-meta">+{addOn.duration} min</span>}
                      <span className="addon-price">{addOn.price > 0 ? `+$${addOn.price}` : 'Free'}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Optional add-ons for wax */}
        {optionalAddOns.length > 0 && onToggleOptionalAddOn && (
          <>
            <div style={{ marginTop: 16, marginBottom: 8, fontSize: 13, fontWeight: 700, letterSpacing: '1.5px', color: '#1a1a1a', textTransform: 'uppercase', padding: '8px 10px', background: '#f3eef0', borderRadius: 4 }}>
              Add-ons
            </div>
            <div className="addons-list">
              <div className="addons-grid" style={{ gridTemplateColumns: '1fr' }}>
                {optionalAddOns.map(addOn => {
                  const isSelected = selectedOptionalAddOns?.some(a => a.id === addOn.id && a.parentServiceId === currentService.id);
                  return (
                    <div key={addOn.id}
                      className={`addon-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => onToggleOptionalAddOn(addOn, currentService.id)}
                    >
                      <div className={`addon-card-checkbox ${isSelected ? 'checked' : ''}`}>
                        {isSelected && '✓'}
                      </div>
                      <div className="addon-info">
                        <div className="addon-name">{addOn.name}</div>
                        {addOn.description && <div className="addon-desc">{addOn.description}</div>}
                        <div className="addon-bottom">
                          {addOn.duration > 0 && <span className="addon-meta">+{addOn.duration} min</span>}
                          <span className="addon-price">{addOn.price > 0 ? `+$${addOn.price}` : 'Free'}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Default: nails removal type flow — uses the shared kiosk-style screen.
  function selectExclusive(addOn, parentServiceId) {
    // Single-select: deselect any other mandatory removal add-ons for this service first.
    const others = selectedMandatoryAddOns.filter(
      a => a.parentServiceId === parentServiceId && a.id !== addOn.id
    );
    others.forEach(a => onToggleAddOn(a, parentServiceId));
    const alreadySelected = selectedMandatoryAddOns.some(
      a => a.id === addOn.id && a.parentServiceId === parentServiceId
    );
    if (!alreadySelected) onToggleAddOn(addOn, parentServiceId);
  }

  const selectedAddOnIds = new Set(
    selectedMandatoryAddOns
      .filter(a => a.parentServiceId === currentService?.id)
      .map(a => a.id)
  );

  return (
    <div className="step-content">
      <GuidedRemovalTypeScreen
        serviceName={currentService?.name}
        addOns={mandatoryAddOns}
        selectedAddOnIds={selectedAddOnIds}
        parentServiceId={currentService?.id}
        onSelect={selectExclusive}
        onBack={onBack}
        onContinue={onContinue}
      />
    </div>
  );
}

export default RemovalTypeStep;
